import { axiosInstance } from "@/lib/axios";
import { useMutation, useQueryClient } from "@tanstack/react-query";

/**
 * Hook for assigning courses to departments
 */
export const useAssignCourseToDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      courseId,
      departmentId,
    }: {
      courseId: string;
      departmentId: string;
    }) => {
      const { data } = await axiosInstance.post("/api/v1/departments/assign-course", {
        courseId,
        departmentId,
      });
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate department queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["department", variables.departmentId] });
      queryClient.invalidateQueries({ queryKey: ["departments"] });
    },
  });
};

/**
 * Hook for removing courses from departments
 */
export const useRemoveCourseFromDepartment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      courseId,
      departmentId,
    }: {
      courseId: string;
      departmentId: string;
    }) => {
      const { data } = await axiosInstance.delete("/api/v1/departments/remove-course", {
        data: {
          courseId,
          departmentId,
        },
      });
      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate department queries to refresh data
      queryClient.invalidateQueries({ queryKey: ["department", variables.departmentId] });
      queryClient.invalidateQueries({ queryKey: ["departments"] });
    },
  });
};

/**
 * Hook for requesting course assignment
 */
export const useRequestCourseAssignment = () => {
  return useMutation({
    mutationFn: async (userId: string) => {
      const { data } = await axiosInstance.post(
        `/api/v1/notifications/request-course-assignment/${userId}`
      );
      return data;
    },
  });
};
